import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, CircularProgress, Fab, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Button, Alert, Typography,
} from '@mui/material';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import axios from 'axios';
import TimelineView from '../components/TimelineView';
import { API_URL, getStories, submitStory } from '../api';

const STORY_CATEGORIES = {
  birth: 'Birth', death: 'Passing', education: 'Education', leadership: 'Leadership',
  award: 'Award / Achievement', health: 'Health', government: 'Government',
  politics: 'Politics', military: 'Military', judiciary: 'Judiciary', international: 'International',
};

/**
 * Our History — the curated story of the Mwendwa Kitavi family, now a
 * LIVING history: members submit stories, the admin approves them, and
 * they take their place in the timeline.
 */
const HistoryPage = () => {
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  // Submission dialog
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [year, setYear] = useState('');
  const [category, setCategory] = useState('');
  const [personName, setPersonName] = useState('');
  const [description, setDescription] = useState('');
  const [details, setDetails] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [peopleRes, storiesData] = await Promise.all([
        axios.get(`${API_URL}/api/people`),
        getStories().catch(() => []),
      ]);
      setPeople(peopleRes.data.filter((p) => p && p._id && p.name));
      setStories(storiesData);
    } catch (err) {
      // Timeline can still render its curated events without stories
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    setError('');
    setSaving(true);
    try {
      const data = await submitStory({ title, year, category, personName, description, details });
      setSuccess(data.message);
      setTitle(''); setYear(''); setCategory(''); setPersonName(''); setDescription(''); setDetails('');
      setTimeout(() => { setOpen(false); setSuccess(''); }, 2200);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not submit the story. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1150, mx: 'auto', px: { xs: 0.5, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      <Box sx={{ px: { xs: 1.5, sm: 1 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Our History</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The story of Chief Mwendwa Kitavi and the generations after him — and yours to add to.
        </Typography>
      </Box>
      <TimelineView
        data={people}
        extraEvents={stories}
        selectedPerson={selectedPerson}
        onPersonClick={(person) => {
          const personData = typeof person === 'object' && person._id ? person : people.find((p) => p._id === person);
          if (personData) setSelectedPerson(personData);
        }}
        onBack={() => navigate('/')}
        isMobile={isMobile}
      />

      {/* Add-to-history */}
      <Fab
        variant="extended"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed', bottom: isMobile ? 76 : 24, right: 16, zIndex: 1250,
          background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)', color: '#fff',
          '&:hover': { background: 'linear-gradient(135deg, #047857 0%, #047857 100%)' },
        }}
      >
        <HistoryEduIcon sx={{ mr: 1 }} />
        {isMobile ? 'Add' : 'Add to our history'}
      </Fab>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add to our family history</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Share a moment worth remembering — a birth, an achievement, a passing,
            a story the elders tell. The family admin will review it before it
            joins the timeline.
          </Typography>
          <TextField label="Title" fullWidth margin="dense" value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. First university graduate in the family" />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Year" type="number" margin="dense" sx={{ width: 140 }}
              value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 1978" />
            <TextField select label="Category" fullWidth margin="dense" value={category}
              onChange={(e) => setCategory(e.target.value)}>
              {Object.entries(STORY_CATEGORIES).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField label="Who is it about? (optional)" fullWidth margin="dense"
            value={personName} onChange={(e) => setPersonName(e.target.value)}
            placeholder="e.g. Ngala Mwendwa" />
          <TextField label="The story" fullWidth margin="dense" multiline minRows={3}
            value={description} onChange={(e) => setDescription(e.target.value)} />
          <TextField label="More details (optional)" fullWidth margin="dense" multiline minRows={2}
            value={details} onChange={(e) => setDetails(e.target.value)} />
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 1 }}>{success}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}
            disabled={saving || !title.trim() || !year || !category || !description.trim()}
            sx={{ background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)' }}>
            {saving ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Submit for approval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoryPage;
